import { cacheKeys } from "@constants/cache-keys";
import { API_BASE_URL } from "@constants/urls";
import authAxios from "@helpers/lib/axios";
import useClerkUser from "@hooks/useClerkUser";
import { isDev } from "@memorang/constants/src/urls";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { useAppStore } from "./useAppStore";

const getMigrationUrl = (tenantName: string) => {
	switch (tenantName.toLowerCase()) {
		case "psi":
			return `${API_BASE_URL}/psi-migration/psi-user-assessment`;
		default:
			return `${API_BASE_URL}/migration/user-assessment`;
	}
};
const REFRESH_INTERVAL = 3000;
const useMigration = () => {
	const { userLogicalId, userPublicMetadataId } = useClerkUser();
	const tenant = useAppStore((store) => store.tenant);

	const fetchMigrationStatus = useCallback(async () => {
		const response = await authAxios.post(
			`${API_BASE_URL}/user-events-v2/v2/get-user-migrated-status`,
			{
				userId: userPublicMetadataId,
			},
		);
		const isMigrated = response.data.data;

		return isMigrated;
	}, [userPublicMetadataId]);

	const triggerMigration = useCallback(async () => {
		const hasAlreadyMigrated = await fetchMigrationStatus();
		if (hasAlreadyMigrated) {
			return;
		}
		const url = getMigrationUrl(tenant?.name ?? "");
		const response = await authAxios.post(url, {
			userId: userLogicalId,
		});
		const data = await response.data;
		return data;
	}, [fetchMigrationStatus, userLogicalId, tenant?.name]);

	const { data: isMigrated } = useQuery({
		queryKey: [cacheKeys.migration, userLogicalId, userPublicMetadataId],
		queryFn: () => {
			const result = fetchMigrationStatus();
			return result;
		},
		refetchInterval: ({ state }) => {
			return state?.data === true || isDev ? false : REFRESH_INTERVAL;
		},
		staleTime: 0,
		enabled: !!userLogicalId && !!userPublicMetadataId && !isDev,
	});

	useEffect(() => {
		if (isMigrated === false) {
			triggerMigration();
		}
	}, [isMigrated, triggerMigration]);

	return { isMigrated };
};

export default useMigration;
